import { useState, useEffect } from "react";
import { useLocation, Link } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { useBranding } from "@/hooks/useBranding";
import GlobalSearch from "./GlobalSearch";
import CommandPalette from "./CommandPalette";

// ─── Clientes ─────────────────────────────────────────────────────────────────
// Antes: hardcoded com EmiteAí + Freitas. Agora puxa via trpc.clients.list
// dentro do componente (raz admin enxerga todos; user comum só o próprio).
// Cores default por id quando primaryColor for null.

const DEFAULT_COLORS = [
  "#3F3F46", "#71717A", "#10B981", "#0A66C2",
  "#7C3AED", "#EC4899", "#0891B2", "#DC2626",
];
function clientColor(c: { id: number; primaryColor?: string | null }): string {
  return c.primaryColor || DEFAULT_COLORS[(c.id - 1) % DEFAULT_COLORS.length];
}

// ─── Navegação ────────────────────────────────────────────────────────────────

type NavItem = {
  label: string;
  href: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
  razAdminOnly?: boolean;
  razEmailOnly?: boolean;
};

const Icon = ({ d }: { d: string }) => (
  <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);

const NAV_ITEMS: NavItem[] = [
  { label: "Início",            href: "/",                icon: <Icon d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /> },
  { label: "Pautas",            href: "/pautas",          icon: <Icon d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /> },
  // Temas escondido do menu (sugestão Vanessa, jun/26) — fluxo migrado pra
  // coluna "Sugestão de Tema" no kanban /pautas. Rota /temas continua viva
  // pra histórico + lixeira (acesso direto pela URL).
  // { label: "Temas",             href: "/temas",           icon: <Icon d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /> },
  // Banco de Ideias, Insights, UTMs e Métricas escondidos do menu a pedido
  // do Rafa (29-mai-2026). Rotas + páginas + DB continuam — basta descomentar
  // se quiser voltar.
  // { label: "Banco de Ideias",   href: "/banco-ideias",    icon: ... },
  { label: "Linha Editorial",   href: "/linha-editorial", icon: <Icon d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" /> },
  { label: "Calendário",        href: "/calendario",      icon: <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /> },
  { label: "Preview Feed",      href: "/preview-feed",    icon: <Icon d="M4 6h16M4 10h16M4 14h16M4 18h16" /> },
  // { label: "Métricas",          href: "/metricas",        icon: ... },
  { label: "Metas & KPIs",      href: "/metas",           icon: <Icon d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /> },
  // { label: "Insights",          href: "/insights",        icon: ... },
  // { label: "UTMs",              href: "/utms",            icon: ... },
  { label: "Relatórios",        href: "/relatorios",      icon: <Icon d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /> },
  { label: "Direcionador",      href: "/direcionador",    razEmailOnly: true, icon: <Icon d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /> },
  { label: "Radar",             href: "/radar",           razEmailOnly: true, icon: <Icon d="M12 12l4-4m5 4a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" /> },
  { label: "Recusas",           href: "/recusas",         razEmailOnly: true, icon: <Icon d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /> },
  { label: "Usuários",          href: "/usuarios",        adminOnly: true, icon: <Icon d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /> },
  { label: "Convites",          href: "/convites",        adminOnly: true,    icon: <Icon d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /> },
  { label: "Admin (RAZ)",       href: "/admin",           razAdminOnly: true, icon: <Icon d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065zM15 12a3 3 0 11-6 0 3 3 0 016 0z" /> },
  { label: "RAZ Master",        href: "/raz-master",      razAdminOnly: true, icon: <Icon d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /> },
  { label: "ClickUp",           href: "/clickup",         razAdminOnly: true, icon: <Icon d="M5 13l4 4L19 7" /> },
];

// Agrupa o menu em seções (antes: 1 lista chapada de até 16 itens sob "Menu").
// Conteúdo = trabalho do cliente; Análise = leitura; Raz e Gestão = bastidor.
const NAV_GROUPS: { title: string; hrefs: string[] }[] = [
  { title: "Conteúdo", hrefs: ["/", "/pautas", "/linha-editorial", "/calendario", "/preview-feed"] },
  { title: "Análise",  hrefs: ["/metas", "/relatorios"] },
  { title: "Raz",      hrefs: ["/direcionador", "/radar", "/recusas"] },
  { title: "Gestão",   hrefs: ["/usuarios", "/convites", "/admin", "/raz-master", "/clickup"] },
];

// ─── AppLayout ─────────────────────────────────────────────────────────────────

interface Props { children: React.ReactNode; }

export default function AppLayout({ children }: Props) {
  const { session, logout, setActiveClient } = useAuth();
  const [location] = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [clientMenuOpen, setClientMenuOpen] = useState(false);
  const [cmdkOpen, setCmdkOpen] = useState(false);

  // Command palette global: Cmd/Ctrl+K abre/fecha.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdkOpen(o => !o);
      }
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, []);

  const activeClientId = session?.activeClientId ?? session?.clientId ?? 1;

  // Aplica branding do cliente ativo: sobrescreve --primary/--secondary/etc
  // como CSS vars. Roda em toda página protegida porque AppLayout é o wrapper.
  useBranding();

  const { data: pautaCounts } = trpc.pautas.counts.useQuery(
    { clientId: activeClientId },
    { enabled: !!session && !!activeClientId, refetchInterval: 60_000 }
  );
  // Badge soma as 2 fases de aprovação (Redação + Design)
  const revisaoCount = (pautaCounts?.["revisao"] ?? 0) + (pautaCounts?.["revisao_design"] ?? 0);

  // Radar: contagem de cards estourados (só Raz) pro badge do menu
  const razEmail = (session?.email ?? "").toLowerCase().trim().endsWith("@razconsulting.com.br");
  const { data: radarCount } = trpc.radar.count.useQuery(undefined, {
    enabled: !!session && razEmail,
    refetchInterval: 60_000,
  });
  const radarVermelhos = radarCount?.vermelhos ?? 0;

  // Lista dinâmica de clientes — substitui o CLIENTS hardcoded antigo.
  // Raz admin: vê todos (clients.list). User comum: só vê o próprio tenant.
  const { data: allClientsRaw } = trpc.clients.list.useQuery(undefined, {
    enabled: !!session?.isRazAdmin,
    staleTime: 5 * 60_000,
  });
  const clients = (allClientsRaw ?? [])
    .filter((c: any) => c.ativo !== false)
    .map((c: any) => ({ id: c.id as number, name: c.nome as string, slug: c.slug as string, color: clientColor(c) }));

  if (!session) return null;

  const activeClient = clients.find(c => c.id === activeClientId);
  const isRazEmail = (session.email ?? "").toLowerCase().trim().endsWith("@razconsulting.com.br");
  const visibleItems = NAV_ITEMS.filter(item => {
    if (item.razEmailOnly && !isRazEmail) return false;
    if (item.razAdminOnly && !session.isRazAdmin) return false;
    if (item.adminOnly && !(session.isRazAdmin || session.role === "admin")) return false;
    return true;
  });

  // Rótulo da rota atual pro breadcrumb do topbar (deriva de NAV_ITEMS).
  const labelFor = (loc: string): string => {
    const item = NAV_ITEMS.find(i => i.href !== "/" && (loc === i.href || loc.startsWith(i.href + "/")));
    if (item) return item.label;
    const seg = loc.split("/").filter(Boolean)[0] ?? "";
    return seg ? seg.charAt(0).toUpperCase() + seg.slice(1) : "";
  };
  const currentLabel = labelFor(location);

  // Recolher a sidebar pra um trilho de ícones (pedido João). Persiste por viewer.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem("sh_sidebar_collapsed") === "1"; } catch { return false; }
  });
  const toggleCollapsed = () => setCollapsed(c => {
    const n = !c;
    try { localStorage.setItem("sh_sidebar_collapsed", n ? "1" : "0"); } catch { /* ignore */ }
    return n;
  });

  const renderNavItem = (item: NavItem) => {
    const isActive = item.href === "/"
      ? location === "/"
      : location === item.href || location.startsWith(item.href + "/");
    return (
      <Link
        key={item.href}
        href={item.href}
        onClick={() => setSidebarOpen(false)}
        title={collapsed ? item.label : undefined}
        className={cn(
          "flex items-center gap-2.5 px-3 py-2 rounded-xl text-[13px] font-medium transition-all group relative",
          collapsed && "justify-center px-0",
          isActive
            ? "bg-[var(--solar-ink)] text-white shadow-[var(--elev-2)]"
            : "text-[var(--sidebar-foreground)] hover:bg-[var(--sidebar-accent)] hover:text-[var(--solar-ink)]"
        )}
      >
        <span className={cn(
          "w-[18px] h-[18px] flex items-center justify-center shrink-0 transition-colors",
          isActive ? "text-white" : "text-[var(--solar-mute)] group-hover:text-[var(--solar-ink)]"
        )}>
          {item.icon}
        </span>
        {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
        {!collapsed && item.href === "/pautas" && revisaoCount > 0 && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: isActive ? "rgba(255,255,255,.22)" : "var(--brand)", color: "#fff", minWidth: 18, textAlign: "center" }}>{revisaoCount}</span>
        )}
        {!collapsed && item.href === "/radar" && radarVermelhos > 0 && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: "#DC2626", color: "white", minWidth: 18, textAlign: "center" }}>{radarVermelhos}</span>
        )}
        {collapsed && ((item.href === "/pautas" && revisaoCount > 0) || (item.href === "/radar" && radarVermelhos > 0)) && (
          <span className="absolute top-1 right-1 w-2 h-2 rounded-full" style={{ background: item.href === "/radar" ? "#DC2626" : "var(--brand)" }} />
        )}
      </Link>
    );
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* ── Mobile overlay ── */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Sidebar (SolarEdge: clara, ícones pílula, ativo preto) ── */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-30 w-64 flex flex-col transition-all duration-200",
          "lg:static lg:translate-x-0",
          collapsed ? "lg:w-[68px]" : "lg:w-64",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
        style={{ background: "var(--sidebar)", borderRight: "1px solid var(--sidebar-border)" }}
      >
        {/* Brand + toggle recolher */}
        <div className={cn("h-16 flex items-center shrink-0", collapsed ? "px-3 justify-center" : "px-5")}>
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--solar-ink)" }}>
              <span className="text-white font-extrabold text-base leading-none">s</span>
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <p className="text-[var(--solar-ink)] font-extrabold text-[15px] leading-none tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>socialhub</p>
                <p className="text-[var(--solar-mute)] text-[10px] tracking-[0.18em] uppercase leading-none mt-1">Social Studio</p>
              </div>
            )}
          </div>
          {!collapsed && (
            <button onClick={toggleCollapsed} title="Recolher menu" className="hidden lg:flex ml-auto w-7 h-7 items-center justify-center rounded-lg text-[var(--solar-mute)] hover:bg-[var(--sidebar-accent)] hover:text-[var(--solar-ink)] transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" /></svg>
            </button>
          )}
        </div>
        {collapsed && (
          <button onClick={toggleCollapsed} title="Expandir menu" className="hidden lg:flex mx-auto mb-1 w-8 h-8 items-center justify-center rounded-lg text-[var(--solar-mute)] hover:bg-[var(--sidebar-accent)] hover:text-[var(--solar-ink)] transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
          </button>
        )}

        {/* Client switcher */}
        <div className={cn("shrink-0", collapsed ? "px-3 pt-1 pb-2 flex justify-center" : "px-3 pt-2 pb-3")}>
          {collapsed ? (
            <button onClick={toggleCollapsed} title={activeClient?.name ?? "Todos os clientes"}
              className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "var(--solar-card)", border: "1px solid var(--sidebar-border)" }}>
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: activeClient?.color ?? "var(--solar-mute)" }} />
            </button>
          ) : session.isRazAdmin ? (
            <div className="relative">
              <button
                onClick={() => setClientMenuOpen(o => !o)}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-2xl text-left transition-colors hover:bg-[var(--sidebar-accent)]"
                style={{ background: "var(--solar-card)", border: "1px solid var(--sidebar-border)" }}
              >
                {activeClient && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: activeClient.color }} />}
                <span className="text-[var(--solar-ink)] text-[13px] font-semibold flex-1 truncate">
                  {activeClient?.name ?? "Todos os clientes"}
                </span>
                <svg className="w-3.5 h-3.5 text-[var(--solar-mute)] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {clientMenuOpen && (
                <div className="absolute top-full left-0 right-0 mt-1.5 rounded-2xl overflow-hidden shadow-lg z-10 bg-[var(--solar-card)] border border-[var(--sidebar-border)]">
                  <button
                    onClick={() => { setActiveClient(null); setClientMenuOpen(false); }}
                    className={cn(
                      "w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] transition-colors",
                      session.activeClientId === null ? "bg-[var(--sidebar-accent)] text-[var(--solar-ink)] font-semibold" : "text-[var(--solar-mute)] hover:bg-[var(--sidebar-accent)] hover:text-[var(--solar-ink)]"
                    )}
                  >
                    <span className="w-2 h-2 rounded-full bg-[var(--solar-mute)] shrink-0" />
                    Todos os clientes
                  </button>
                  {clients.map(c => (
                    <button
                      key={c.id}
                      onClick={() => { setActiveClient(c.id); setClientMenuOpen(false); }}
                      className={cn(
                        "w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] transition-colors",
                        session.activeClientId === c.id ? "bg-[var(--sidebar-accent)] text-[var(--solar-ink)] font-semibold" : "text-[var(--solar-mute)] hover:bg-[var(--sidebar-accent)] hover:text-[var(--solar-ink)]"
                      )}
                    >
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: c.color }} />
                      {c.name}
                    </button>
                  ))}
                  {clients.length === 0 && (
                    <p className="px-3.5 py-2.5 text-[12px] text-[var(--solar-mute)] italic">
                      Carregando clientes…
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            activeClient && (
              <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-2xl" style={{ background: "var(--solar-card)", border: "1px solid var(--sidebar-border)" }}>
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: activeClient.color }} />
                <span className="text-[var(--solar-ink)] text-[13px] font-semibold truncate">{activeClient.name}</span>
              </div>
            )
          )}
        </div>

        {/* Busca global de posts/tarefas (lupa) */}
        {!collapsed && <GlobalSearch isRaz={!!session.isRazAdmin} onNavigate={() => setSidebarOpen(false)} />}

        {/* Nav agrupado — seções em vez de uma lista chapada gigante */}
        <nav className="flex-1 px-3 py-1 overflow-y-auto">
          {NAV_GROUPS.map(group => {
            const items = visibleItems.filter(it => group.hrefs.includes(it.href));
            if (!items.length) return null;
            return (
              <div key={group.title} className="mb-1.5">
                {collapsed
                  ? <div className="h-px mx-2 my-2" style={{ background: "var(--sidebar-border)" }} />
                  : <p className="text-[var(--solar-mute)] text-[10px] uppercase tracking-[0.16em] px-3 pt-3 pb-1.5 font-semibold">{group.title}</p>}
                <div className="space-y-0.5">{items.map(renderNavItem)}</div>
              </div>
            );
          })}
          {(() => {
            const grouped = new Set(NAV_GROUPS.flatMap(g => g.hrefs));
            const rest = visibleItems.filter(it => !grouped.has(it.href));
            return rest.length ? <div className="space-y-0.5 mt-1">{rest.map(renderNavItem)}</div> : null;
          })()}
        </nav>

        {/* User + logout */}
        <div className="px-3 pb-4 pt-3 shrink-0" style={{ borderTop: "1px solid var(--sidebar-border)" }}>
          <div className={cn("flex items-center gap-3 py-2 mb-1", collapsed ? "px-0 justify-center" : "px-3")} title={collapsed ? `${session.name} · ${session.email}` : undefined}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-white text-xs font-bold" style={{ background: "var(--solar-ink)" }}>
              {(session.name ?? session.email).charAt(0).toUpperCase()}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-[var(--solar-ink)] text-[13px] font-semibold truncate">{session.name}</p>
                <p className="text-[var(--solar-mute)] text-[11px] truncate">{session.email}</p>
              </div>
            )}
          </div>
          <button
            onClick={logout}
            title={collapsed ? "Sair" : undefined}
            className={cn("w-full flex items-center gap-3 py-2 rounded-xl text-[13px] text-[var(--solar-mute)] hover:bg-[var(--sidebar-accent)] hover:text-[var(--solar-ink)] transition-colors", collapsed ? "px-0 justify-center" : "px-3")}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            {!collapsed && "Sair"}
          </button>
        </div>
      </aside>

      {/* ── Content ── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar mobile */}
        <header className="h-14 flex items-center gap-4 px-4 lg:hidden shrink-0 border-b border-[var(--solar-line)] bg-[var(--solar-card)]">
          <button onClick={() => setSidebarOpen(true)} className="text-[var(--solar-ink)]">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-[var(--solar-ink)]">
              <span className="text-white font-extrabold text-sm leading-none">s</span>
            </div>
            <span className="font-extrabold text-[15px] tracking-tight text-[var(--solar-ink)]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              socialhub
            </span>
          </div>
        </header>

        {/* Topbar desktop: breadcrumb + contexto de cliente sempre visível.
            Resolve o wayfinding (o usuário sempre sabe onde está e em qual
            cliente opera), sem tocar no header próprio de cada página. */}
        <header className="hidden lg:flex items-center gap-4 h-14 px-6 shrink-0 bg-[var(--solar-card)] border-b border-[var(--solar-line)]">
          <nav className="flex items-center gap-2 text-[13px] min-w-0" aria-label="Trilha de navegação">
            <Link href="/" className="flex items-center gap-1.5 text-[var(--solar-mute)] hover:text-[var(--solar-ink)] transition-colors shrink-0">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span>Início</span>
            </Link>
            {location !== "/" && currentLabel && (
              <>
                <svg className="w-3.5 h-3.5 text-[var(--solar-line)] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <span className="font-semibold text-[var(--solar-ink)] truncate" style={{ fontFamily: "Poppins, sans-serif" }}>{currentLabel}</span>
              </>
            )}
          </nav>
          <div className="flex-1" />
          <button
            onClick={() => setCmdkOpen(true)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[13px] text-[var(--solar-mute)] bg-[var(--solar-bg)] border border-[var(--solar-line)] hover:border-[var(--solar-mute)] transition-colors shrink-0"
            title="Buscar tudo (Cmd/Ctrl+K)"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" /></svg>
            <span>Buscar</span>
            <kbd className="text-[10px] border border-[var(--solar-line)] rounded px-1 py-0.5 leading-none">⌘K</kbd>
          </button>
          {activeClient && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--solar-bg)] border border-[var(--solar-line)] shrink-0">
              <span className="text-[10px] uppercase tracking-wider text-[var(--solar-mute)] font-semibold">Cliente</span>
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: activeClient.color }} />
              <span className="text-[13px] font-semibold text-[var(--solar-ink)] max-w-[180px] truncate">{activeClient.name}</span>
            </div>
          )}
        </header>

        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>

      {cmdkOpen && <CommandPalette isRaz={!!session.isRazAdmin} onClose={() => setCmdkOpen(false)} />}
    </div>
  );
}
